import {
  getStationDisplayProps,
  STATION_LINE_LIMIT_PAUSED,
} from "../../utils/getStationState";
import { formatDate, formatTime } from "./format";
import { formatCurrentValue } from "../../utils/formatCurrent";
import { MyPlace, MyStation } from "./types";

/** `DA.1`: ток станции на экране — «9,3» при десятых, иначе целые. */
export function currentTextOf(station: MyStation): string {
  return formatCurrentValue(station.current_a, station.current_dA) ?? "—";
}

/**
 * `Ф1` (кабинет ПЗС): правила показа раздела «Станции».
 *
 * Отдельно от компонента, чтобы проверялось без браузера: какие места попадают
 * в список, что делать с местом без станции, закрытой связью и станцией не на
 * связи. Статус станции берётся у `getStationDisplayProps` — порядок
 * приоритетов тот же, что на сайте персонала; `status_id` собирается из `online`
 * (в DTO кабинета его нет).
 */

export const STATIONS_REFRESH_MS = 15000;

export type PlaceViewKind = "station" | "waiting-act" | "pending" | "closed";

export type PlaceView = {
  key: string;
  placeId: number;
  number: string | null;
  plantName: string | null;
  place: MyPlace | null;
  station: MyStation | null;
  kind: PlaceViewKind;
};

/** Состояние станции словами; приоритеты — как в `getStationDisplayProps`. */
export function stationStatusOf(station: MyStation): {
  label: string;
  hint: string | null;
  color: string;
} {
  const display = getStationDisplayProps({
    status_id: station.online === false ? 0 : 1,
    state_id: station.state_id ?? 0,
    gps: station.error?.active ? station.error.code ?? "ошибка" : null,
    last_session: station.last_session,
  });

  // 1. Нет связи с модулем
  if (station.state_id === 6) {
    return {
      label: "Нет связи с модулем",
      hint: "Модуль не отвечает — данные станции недоступны",
      color: display.color,
    };
  }

  // 2. Станция не на связи — с какого времени нет данных.
  //    `E.2`: пока станция не выходила на связь ни разу, времени нет — «с —»
  //    читалось как сбой показа, поэтому текстом названо состояние.
  if (station.online === false) {
    if (!station.last_data_at) {
      return {
        label: "Станция ещё не выходила на связь",
        hint: "Данные появятся после первого сеанса связи",
        color: display.color,
      };
    }

    return {
      label: `Нет связи со станцией с ${formatTime(station.last_data_at)}`,
      hint: "Данные не обновляются: связь со станцией потеряна",
      color: display.color,
    };
  }

  // 3. Ошибка станции (код из `error`, без служебного текста модуля)
  if (station.error?.active) {
    return {
      label: "Ошибка станции",
      hint: "Станция сообщила об ошибке — данные могут быть неполными",
      color: display.color,
    };
  }

  return {
    label: display.state,
    // `E.2`: подсказка — у каждого состояния своя; у «ожидания заряда» она
    // приходит из `getStationDisplayProps` (`С2`).
    hint: HINTS_BY_STATE[display.state] ?? display.hint,
    color: display.color,
  };
}

/**
 * `E.2` (Ф1): подсказка к каждому состоянию станции.
 *
 * Одно место на весь раздел: подсказка не должна зависеть от того, каким
 * путём состояние получено (офлайн, ошибка, рабочее).
 */
export const HINTS_BY_STATE: Record<string, string> = {
  свободно: "Место свободно — можно подключать автомобиль",
  "не готова": "Станция не настроена — обратитесь к администратору",
  ожидание: "Автомобиль подключён. Заряд начнётся, когда будет разрешение",
  [STATION_LINE_LIMIT_PAUSED]: "Заряд приостановлен лимитом линии и продолжится сам",
  заряжается: "Идёт заряд",
  завершено: "Заряд завершён — отключите кабель",
  недоступно: "Станция не готова к заряду",
};

/**
 * `E.2`: доля «ток / лимит» для полосы — от 0 до 1.
 *
 * Нет числа или лимит не задан — `null`: полоса не рисуется, а не показывается
 * пустой (пустая полоса читается как «ток 0»).
 */
export function currentShareOf(station: MyStation): number | null {
  const hasDeca =
    station.current_dA !== null &&
    station.current_dA !== undefined &&
    Number.isFinite(Number(station.current_dA));
  const current = hasDeca
    ? Number(station.current_dA) / 10
    : Number(station.current_a);
  const limit = Number(station.limit_current);

  if (
    (!hasDeca && station.current_a === null) ||
    station.limit_current === null ||
    !Number.isFinite(current) ||
    !Number.isFinite(limit) ||
    limit <= 0
  ) {
    return null;
  }

  return Math.max(0, Math.min(1, current / limit));
}

/** Места и станции → карточки: станция, ждёт акта, связь ещё не началась, закрыта. */
export function buildPlaceViews(
  stations: MyStation[],
  places: MyPlace[],
): PlaceView[] {
  const stationByPlaceId = new Map<number, MyStation>();

  for (const station of stations) {
    if (station.place?.id !== null && station.place?.id !== undefined) {
      stationByPlaceId.set(Number(station.place.id), station);
    }
  }

  const views: PlaceView[] = [];

  for (const place of places) {
    const placeId =
      place.place?.id !== null && place.place?.id !== undefined
        ? Number(place.place.id)
        : null;

    if (placeId === null) continue;

    const station = stationByPlaceId.get(placeId) ?? null;
    stationByPlaceId.delete(placeId);

    const kind: PlaceViewKind =
      place.state === "closed" || place.state === "cancelled"
        ? "closed"
        : place.state === "pending"
          ? "pending"
          : station
            ? "station"
            : "waiting-act";

    views.push({
      key: `place-${placeId}`,
      placeId,
      number: place.place?.number ?? null,
      plantName: place.place?.plant?.name ?? null,
      place,
      station,
      kind,
    });
  }

  // Станция без строки связи в `/my/places` (сегодня не бывает, но ответы двух
  // маршрутов могут разойтись по времени) — карточка остаётся, не теряется.
  for (const [placeId, station] of stationByPlaceId) {
    views.push({
      key: `place-${placeId}`,
      placeId,
      number: station.place?.number ?? null,
      plantName: station.plant?.name ?? null,
      place: null,
      station,
      kind: "station",
    });
  }

  return views.sort((left, right) =>
    String(left.number ?? "").localeCompare(String(right.number ?? ""), "ru", {
      numeric: true,
    }),
  );
}

/**
 * `E.1` (`Л5`, Ф1/Ф3): договор в карточке места — «Договор № … от …».
 *
 * Сервер отдаёт `contract {number, date}` из связи (`my-places-service.js:131–133`)
 * с 22.09 (`6407e35`); в этой карточке поле не читалось вовсе — отсюда «договор
 * не отдаётся» в отчёте 21.10. Нет номера — прочерк, а не пустая строка: пустое
 * место человек читает как «не загрузилось».
 */
export function contractLabelOf(
  contract: { number: string | null; date: string | null } | null | undefined,
): string {
  const number = (contract?.number ?? "").trim();

  if (!number) return "—";

  return contract?.date
    ? `Договор № ${number} от ${formatDate(contract.date)}`
    : `Договор № ${number}`;
}

/** Русская форма слова по числу: 1 место, 2 места, 5 мест. */
function plural(count: number, one: string, few: string, many: string): string {
  const tail = count % 100;

  if (tail >= 11 && tail <= 14) return many;

  switch (count % 10) {
    case 1:
      return one;
    case 2:
    case 3:
    case 4:
      return few;
    default:
      return many;
  }
}

/** «3 места на 2 объектах» — для подзаголовка раздела. */
export function placesSummaryOf(views: PlaceView[]): string {
  const places = views.length;
  const plants = new Set(
    views.map((view) => view.plantName).filter((name) => Boolean(name)),
  ).size;

  return `${places} ${plural(places, "место", "места", "мест")} на ${plants} ${plural(
    plants,
    "объекте",
    "объектах",
    "объектах",
  )}`;
}
